import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";

import DstButton from "../../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import RequiredLabel from "../../../../../components/RequiredLabel/RequiredLabel";
import { LanguageEnum } from "../../../../../models/Language";
import { ProgrammingLanguageEnum } from "../../../../../models/ProgrammingLanguage";
import { PurposeEnum } from "../../../../../models/Purpose";
import translate from "../../../../../services/Translate";
import { ModuleFormData } from "../ModuleGeneralInformation.d";
import { UpdateModalProps } from "./UpdateModal.d";
import { fetchPrograms, updateModule } from "./UpdateModal.function";

const UpdateModal = ({ show, onClose, moduleData, language, onEditSave }: UpdateModalProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [programs, setPrograms] = useState<any[]>([]);
  const [owner, setOwner] = useState<any | null>(null);
  const [form, setForm] = useState<ModuleFormData>({
    description: "",
    image_url: "",
    is_deprecated: false,
    language: LanguageEnum.FR,
    name: "",
    origin_program: null,
    owner: null,
    programming_language: ProgrammingLanguageEnum.OTHER,
    purpose: PurposeEnum.OTHER,
  });
  const _programItems = programs.map((program) => ({ id: program.id, name: program.name }));

  useEffect(() => {
    setForm({
      description: moduleData?.description ?? "",
      image_url: moduleData?.image_url ?? "",
      is_deprecated: !!moduleData?.is_deprecated,
      language: moduleData?.language ?? LanguageEnum.FR,
      name: moduleData?.name ?? "",
      origin_program: moduleData?.origin_program?.id ?? null,
      owner: moduleData?.owner?.id ?? null,
      programming_language: moduleData?.programming_language ?? ProgrammingLanguageEnum.OTHER,
      purpose: moduleData?.purpose ?? PurposeEnum.OTHER,
    });
    setOwner(moduleData?.owner ?? null);
  }, [moduleData]);

  useEffect(() => {
    fetchPrograms(setPrograms, setProgramsLoading, language);
  }, [show, language]);

  const _handleChange = (key: string, value: any) => {
    setForm((previousData) => ({ ...previousData, [key]: value }));
  };

  const _handleSubmit = () => {
    updateModule(
      moduleData.id,
      form,
      setIsEditing,
      () => {
        onEditSave?.();
        onClose?.();
      },
      language
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.MODULE.GENERAL_INFO.EDIT_POPUP.TITLE")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.MODULE.GENERAL_INFO.NAME")} />
            <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.MODULE.GENERAL_INFO.DESCRIPTION")} />
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(event) => _handleChange("description", event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.MODULE.GENERAL_INFO.PROGRAMMING_LANGUAGE")} />
            <Form.Select
              value={form.programming_language}
              onChange={(event) => _handleChange("programming_language", event.target.value)}
            >
              {Object.values(ProgrammingLanguageEnum).map((programmingLanguage) => (
                <option key={programmingLanguage} value={programmingLanguage}>
                  {programmingLanguage}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.MODULE.GENERAL_INFO.OWNER")} />
            <MultiSelectLiveDropdown
              category={["users"]}
              language={language}
              isSingleSelect
              label={
                owner
                  ? `${owner.first_name} ${owner.last_name}`
                  : translate(language, "PAGES.MODULE.GENERAL_INFO.EDIT_POPUP.SELECT_OWNER")
              }
              toggleClass="w-100 border"
              initialSelectedItems={
                moduleData?.owner
                  ? [
                      {
                        value: moduleData.owner,
                        category: "users",
                      },
                    ]
                  : []
              }
              onConfirm={(selectedUsers: any[]) => {
                const selectedOwner = selectedUsers.length ? selectedUsers[0].value : null;
                setOwner(selectedOwner);
                setForm((prev) => ({ ...prev, owner: selectedOwner?.id ?? null }));
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.MODULE.GENERAL_INFO.LANGUAGE")} />
            <Form.Select value={form.language} onChange={(event) => _handleChange("language", event.target.value)}>
              {Object.values(LanguageEnum).map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.MODULE.GENERAL_INFO.ORIGIN_PROGRAM.TITLE")} />
            <MultiSelectDropdown
              items={_programItems}
              language={language}
              label={
                form.origin_program
                  ? _programItems.find((program) => program.id === Number(form.origin_program))?.name
                  : translate(language, "PAGES.MODULE.GENERAL_INFO.EDIT_POPUP.SELECT_ORIGIN_PROGRAM")
              }
              mock={programsLoading}
              isSingleSelect
              badgeItemProperty="name"
              displayItem={(item: any) => <span>{item.name}</span>}
              onConfirm={([item]: any[]) => _handleChange("origin_program", item?.id ?? null)}
              borderToggle
              btnWidth="100%"
              toggleClass="w-100"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.MODULE.GENERAL_INFO.PURPOSE")} />
            <Form.Select value={form.purpose} onChange={(event) => _handleChange("purpose", event.target.value)}>
              {Object.values(PurposeEnum).map((purpose) => (
                <option key={purpose} value={purpose}>
                  {purpose}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{translate(language, "PAGES.MODULE.GENERAL_INFO.EDIT_POPUP.IMAGE_URL")}</Form.Label>
            <Form.Control value={form.image_url} onChange={(event) => _handleChange("image_url", event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="module-is-deprecated"
              label={translate(language, "PAGES.MODULE.COMMON.DEPRECATED")}
              checked={form.is_deprecated}
              onChange={(event) => _handleChange("is_deprecated", event.target.checked)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <DstButton
          variant="secondary"
          value={translate(language, "PAGES.MODULES.CREATE.BUTTON.CANCEL")}
          clickFunction={onClose}
        />
        <DstButton
          loading={isEditing}
          value={translate(language, "PAGES.MODULE.GENERAL_INFO.EDIT_POPUP.SAVE_CHANGES")}
          clickFunction={_handleSubmit}
          disabled={
            isEditing ||
            !form.owner ||
            !form.name.trim() ||
            !form.description.trim() ||
            !form.language ||
            !form.programming_language ||
            !form.origin_program ||
            !form.purpose
          }
        />
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateModal;
